import { useNotifications } from '@notifications/Notifications';
import { PropsWithChildren, createContext, useCallback, useContext, useMemo } from 'react';
import { ChannelDetailContext } from './graph/ChannelDetailProvider';
import { GraphDataContext } from './graph/GraphDataProvider';
import { Node } from './graph/types';

interface GraphsOutdatedNodesProviderType {
  updateAllNodes: (outdatedNodeIds: string[]) => Promise<void>;
}

export const GraphsOutdatedNodesContext = createContext<GraphsOutdatedNodesProviderType>(
  {} as GraphsOutdatedNodesProviderType,
);

export const GraphsOutdatedNodesProvider = ({ children }: PropsWithChildren) => {
  const { nodes, removeNode, updateNode } = useContext(GraphDataContext);
  const { addNotification, createAsyncNotification } = useNotifications();
  const { channelNodes } = useContext(ChannelDetailContext);

  const removeUndefinedNode = useCallback(
    (node: Node) => {
      if (!node?.name) {
        return;
      }
      removeNode(node?.name);
      addNotification({
        text: `Deleted ${node?.name || ''} node, ${node?.name || ''} does not exist`,
        type: 'error',
        duration: 10000,
      });
    },
    [removeNode, addNotification],
  );

  const updateSingleNode = useCallback(
    (nodeId: string, last = false) => {
      const node = nodes.find((checkNode) => checkNode.name === nodeId);
      const latestNode = channelNodes.find((channelNode) => channelNode.name === node?.nodeClass);

      if (!latestNode) {
        removeUndefinedNode(node);
        return;
      }
      updateNode(nodeId, latestNode, last);
    },
    [nodes, channelNodes, removeUndefinedNode, updateNode],
  );

  const updateAllNodes = useCallback(
    async (outdatedNodeIds: string[]) => {
      if (outdatedNodeIds.length === 0) return;

      const update = createAsyncNotification('All Nodes successfully updated.', () => {
        outdatedNodeIds.forEach((id, index) =>
          updateSingleNode(id, index === outdatedNodeIds.length - 1),
        );
      });
      await update();
    },
    [createAsyncNotification, updateSingleNode],
  );

  const providerValue = useMemo(
    () => ({
      updateAllNodes,
    }),
    [updateAllNodes],
  );

  return (
    <GraphsOutdatedNodesContext.Provider value={providerValue}>
      {children}
    </GraphsOutdatedNodesContext.Provider>
  );
};
